<template>
  <RouteProgressBar></RouteProgressBar>
  <div
    :class="[{ 'theme-da': themeSwitch == 'da' }, { 'theme-zur': themeSwitch == 'zur' }]"
    class="portal-shell zurich-ui-library"
  >
    <div v-if="isStandalone && headerConfig" class="is-styled">
      <Header :config="headerConfig" :is-logged-in="isLoggedIn"></Header>
    </div>
    <div v-if="loadPortal && routerLoaded">
      <div class="wrapper zurich-ui-library">
        <div class="is-styled">
          <div
            v-if="!isDesktopNavi && activeMenuItem && partnerProxyId"
            :class="{
              'navigation-overview': router.currentRoute.value.path === '/ueberblick'
            }"
            class="navigation-mobile"
          >
            <Navigation
              @click="clickNaviLink"
              :activeMenuItem="activeMenuItem"
              :logoutAction="logout"
              :menuItems="
                router
                  .getRoutes()
                  .filter((route) => route.meta?.isNavi)
                  .map((route) => mapRouteToMenuItem(route))
              "
              :positionStyle="positionStyle"
              :topPosition="calculatedTopPos"
              defaultTogglerTitle="Menü"
            ></Navigation>
          </div>

          <AdvertisementConsentModal
            v-if="isAdvertisementConsentModalAllowed"
            :router-loaded="routerLoaded"
          ></AdvertisementConsentModal>

          <HeaderTitle
            v-if="!isPublicPage && partnerProxyId && router.currentRoute.value"
            :style="[parentHasStickyHeader ? { paddingTop: paddingForHeadline } : {}]"
            @click="clickNaviLink"
            :colorable="isHeaderColorable"
            aria-disabled="true"
          >
            <template v-slot:title>
              <Navigation
                v-if="isDesktopNavi && activeMenuItem && partnerProxyId"
                @click="clickNaviLink"
                :activeMenuItem="activeMenuItem"
                :logoutAction="logout"
                :menuItems="
                  router
                    .getRoutes()
                    .filter((route) => route.meta?.isNavi)
                    .map((route) => mapRouteToMenuItem(route))
                "
                :positionStyle="positionStyle"
                :topPosition="calculatedTopPos"
                defaultTogglerTitle="Menü"
              ></Navigation>
              <div
                v-if="router.currentRoute.value.path === '/ueberblick'"
                class="extra-margin-zkp"
              >
                <PartnerGreeting
                  v-if="router.currentRoute.value.path === '/ueberblick'"
                ></PartnerGreeting>
              </div>
              <h1
                v-if="
                  router.currentRoute.value.path !== '/ueberblick' &&
                  router.currentRoute.value.path !== '/registrieren' &&
                  router.currentRoute.value.path !== '/benutzername-vergessen' &&
                  router.currentRoute.value.path !== '/aktivierung'
                "
                class="h1"
              >
                {{ headline }}
              </h1>
            </template>
            <template v-if="showSubline" v-slot:content>
              <p v-if="subline" class="subline" v-dompurify-html="subline"></p>
              <VertragsServicesIconBox
                v-if="
                  router.currentRoute.value.path === '/vertrags-services' ||
                  router.currentRoute.value.path === '/vertragsservices'
                "
              >
              </VertragsServicesIconBox>
            </template>
            <template
              v-if="router.currentRoute.value.path === '/ueberblick'"
              v-slot:teaser
            >
              <TeaserWelcome :isPlacedInHeader="true"></TeaserWelcome>
            </template>
            <template
              v-if="partnerProxyId && router.currentRoute.value.path === '/ueberblick'"
              v-slot:postbox
            >
              <PostboxTile
                class="is-flex"
                :partner-proxy-id="partnerProxyId"
              ></PostboxTile>
            </template>
          </HeaderTitle>
          <div id="wc-outer-wrapper" class="wc-outer-no-padding">
            <div
              v-if="
                router.currentRoute.value.path !== '/einstellungen' &&
                router.currentRoute.value.path !== '/persoenliche-daten' &&
                router.currentRoute.value.path !== '/vertragsServices' &&
                router.currentRoute.value.path !== '/vertrags-services'
              "
              :class="[
                parentHasStickyHeader ? 'wc-outer--parent-has-sticky-header' : '',
                isBodyBackground
                  ? 'wc-outer hide-scrollbar'
                  : isPublicPage
                    ? 'wc-outer-wc-head colorable-public'
                    : 'wc-outer-wc-head colorable',
                loading ? 'loading-style' : ''
              ]"
              id="wc-outer"
            >
              <div class="wc-container">
                <RouterView></RouterView>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <FooterBanner
      v-if="isStandalone && hasFooterBannerText && isFooterBannerVisible"
      :text="footerBannerText"
      @close="closeFooterBanner"
    ></FooterBanner>
    <Footer v-if="isStandalone && footerConfig" :config="footerConfig"></Footer>
    <BackToTop
      v-if="isStandalone"
      :autoFadeOut="true"
      :onlyMobile="false"
      :showAtHeight="300"
    ></BackToTop>
  </div>
</template>

<script lang="ts" setup>
import { type RouteRecordNormalized, RouterView } from 'vue-router'
import { type RouteMetaInfo, router } from '@/router/router'
import TeaserWelcome from '@/componentsTenant/rahmen/TeaserWelcome.vue'
import PartnerGreeting from './components/partner-proxy/PartnerGreeting.vue'
import PostboxTile from './components/postbox/PostboxTile.vue'
import VertragsServicesIconBox from '@/componentsTenant/rahmen/VertragsServicesIconBox.vue'
import AdvertisementConsentModal from '@/components/partner-proxy/AdvertisementConsentModal.vue'
import RouteProgressBar from '@/components/common/RouteProgressBar.vue'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import i18n from '@/i18n'
import { isDa, isTrue } from './utils/CommonUtils'
import { getQueryParameterKey, isStandaloneMode } from './utils/utils'
import { getPartnerProxyId, isAuthenticated, keycloakLogout } from '@/utils/KeycloakUtils'
import {
  BackToTop,
  Footer,
  FooterBanner,
  Header,
  HeaderTitle,
  type MenuItem,
  Navigation
} from 'de-zurich-ui-library'

defineProps({
  loadPortal: {
    type: Boolean,
    required: true,
    default: false
  }
})

const footerBannerText =
  'Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Durch die Nutzung unserer Website stimmen Sie der Verwendung von Cookies zu.'

const themeSwitch = isDa() ? ref('da') : ref('zur')
const isStandalone = computed(() => isStandaloneMode())
const hasFooterBannerText = computed(() => footerBannerText.length > 0)
const isFooterBannerVisible = ref(true)

function mapRouteToMenuItem(route: RouteRecordNormalized): MenuItem {
  const meta = route.meta as RouteMetaInfo
  return {
    name: meta?.naviLabel ?? String(route.name ?? ''),
    path: route.path,
    meta: {
      naviLabel: meta?.naviLabel,
      isNavi: meta?.isNavi,
      requiresAuth: meta?.requiresAuth
    }
  }
}

const getRouteItem = (path: string): MenuItem | undefined => {
  const route =
    router.getRoutes().find((r: RouteRecordNormalized) => r.path === path) ??
    router.getRoutes()[0]
  return route ? mapRouteToMenuItem(route) : undefined
}

const partnerProxyId = computed(() => {
  return getPartnerProxyId() || ''
})

function findRouterForThankYouPage(queryParam: string): MenuItem {
  if (['name-aendern', 'adresse-aendern', 'bankdaten-aendern'].includes(queryParam)) {
    const route = router.getRoutes().find((r) => r.path === '/persoenliches-profil')
    if (route) return mapRouteToMenuItem(route)
  } else if (
    [
      isDa()
        ? 'digitale-schadenkarte-anfordern'
        : 'digitale-versicherungskarte-anfordern',
      'beitragsbescheinigung-anfordern',
      'bescheinigung-fuer-begleitetes-fahren-anfordern',
      'fahrzeug-wechseln',
      'internationale-versicherungskarte-anfordern',
      'vertrag-aendern'
    ].includes(queryParam)
  ) {
    const route = router.getRoutes().find((r) => r.name === 'vertragsServices')
    if (route) return mapRouteToMenuItem(route)
  }
  throw new Error('Query parameter is not valid')
}

const activeMenuItem = computed(() => {
  const currentRoute = router.currentRoute.value
  const currentNaviLabel = (currentRoute.meta as RouteMetaInfo)?.naviLabel
  const naviRoutes = router.getRoutes().filter((route) => route.meta?.isNavi)

  // 1. Wenn die aktuelle Route ein naviLabel hat, das passende Navi-Item darüber finden
  if (currentNaviLabel) {
    const matchedByLabel = naviRoutes.find(
      (route) => (route.meta as RouteMetaInfo)?.naviLabel === currentNaviLabel
    )
    if (matchedByLabel) {
      return mapRouteToMenuItem(matchedByLabel)
    }
  }

  // 2. Sonderfall /danke: aktives Navi-Item über Query-Parameter bestimmen
  if (currentRoute.path === '/danke') {
    const queryParam = Object.keys(currentRoute.query)[0] ?? ''
    return findRouterForThankYouPage(queryParam)
  }

  // 3. Fallback: Pfad-basiertes Matching
  const matchedByPath = naviRoutes.find((route) => currentRoute.path.includes(route.path))
  if (matchedByPath) {
    return mapRouteToMenuItem(matchedByPath)
  }

  // 4. Letzter Fallback: /ueberblick
  const fallback = router.getRoutes().find((route) => route.path === '/ueberblick')
  return fallback ? mapRouteToMenuItem(fallback) : undefined
})

const isPublicPage = computed(() => {
  return !router.currentRoute.value.meta?.requiresAuth
})

const isAdvertisementConsentModalAllowed =
  isTrue(
    globalThis.ENV_CONFIG?.FEATURE_FLAGS.PARTNER_PROXY.ENABLE_ADVERTISEMENT_CONSENT_MODAL
  ) || false

function closeFooterBanner(): void {
  isFooterBannerVisible.value = false
}

const isDesktopNavi = ref(false)
const loading = ref(false)
const offsetForNavigation = ref(0)
const paddingForHeadline = ref('0')
const parentHasStickyHeader = ref(false)
const positionStyle = ref<string | undefined>(undefined)
const topPosition = ref(0)

const logout = async () => {
  isLoggedIn.value = false
  await keycloakLogout(globalThis.ENV_CONFIG?.REDIRECTS.LOGOUT_REDIRECT)
}

const isLoggedIn = ref(isAuthenticated())
const headerConfig = computed(() => {
  const config = globalThis.ENV_CONFIG?.HEADER
  if (!config) return undefined

  return {
    ...config,
    logout: {
      ...config.logout,
      onClick: logout
    }
  }
})
const footerConfig = computed(() => globalThis.ENV_CONFIG?.FOOTER)

const headline = computed(() => {
  let name = router.currentRoute.value.name?.toString()
  const path = router.currentRoute.value.path

  if (router.currentRoute.value.fullPath.includes('/danke')) {
    name = getQueryParameterKey()
  }
  return path === '/' && name && name !== ''
    ? ''
    : String(i18n.global.t('headline.' + name))
})

const subline = computed(() => {
  let name = router.currentRoute.value.name?.toString()
  const path = router.currentRoute.value.path

  if (router.currentRoute.value.fullPath.includes('/danke')) {
    name = getQueryParameterKey()
  }

  if (path === '/' && name && name !== '') {
    return ''
  }

  if (name === 'email') {
    return String(
      i18n.global.t('subline.' + name, [
        globalThis.ENV_CONFIG?.SERVICE_LINKS?.SETTINGS_PAGE?.CHANGE_EMAIL_PAGE_EXTERNAL ??
          ''
      ])
    )
  }

  return String(i18n.global.t('subline.' + name))
})

const calculatedTopPos = computed(() => topPosition.value + offsetForNavigation.value)

const isBodyBackground = computed(
  () =>
    ![
      globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_PAGE,
      globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_REGISTRATION_PAGE
    ].includes(router.currentRoute.value.path)
)
const isHeaderColorable = computed(() =>
  [globalThis.ENV_CONFIG?.REDIRECTS.ACCOUNT_LOGIN_ON_SUCCESS_REDIRECT].includes(
    router.currentRoute.value.path
  )
)

const showSubline = computed(() => {
  return (
    router.currentRoute.value.meta.showSubline ||
    router.currentRoute.value.path.includes('/persoenliche-daten') ||
    router.currentRoute.value.path.includes('/einstellungen') ||
    router.currentRoute.value.path.includes('/vertragsservices') ||
    router.currentRoute.value.fullPath.includes(
      isDa()
        ? 'danke?digitale-schadenkarte-anfordern'
        : 'danke?digitale-versicherungskarte-anfordern'
    ) ||
    router.currentRoute.value.fullPath.includes(
      isDa()
        ? 'danke?internationale-versicherungskarte-anfordern'
        : 'danke?internationale-versicherungskarte-anfordern'
    ) ||
    router.currentRoute.value.path.includes('/vertrags-services')
  )
})

const routerLoaded = ref(false)

function removePreloader(): void {
  const preloader =
    globalThis.document.querySelector('#cp-preloader') ??
    globalThis.document.querySelector('#host')?.shadowRoot?.querySelector('#cp-preloader')

  if (preloader instanceof HTMLElement || preloader instanceof SVGElement) {
    preloader.remove()
  }
}

const handleKeycloakSuccess = async () => {
  isLoggedIn.value = true
  routerLoaded.value = true
  await nextTick()
  removePreloader()
}

const handleKeycloakFail = () => {
  isLoggedIn.value = false
  routerLoaded.value = false
}

const handleLogout = () => {
  isLoggedIn.value = false
}

const handleLoadPublicPages = async () => {
  routerLoaded.value = true
  await nextTick()
  removePreloader()
}

globalThis.addEventListener('cp_keycloak_auth_success', handleKeycloakSuccess)
globalThis.addEventListener('cp_keycloak_auth_fail', handleKeycloakFail)
globalThis.addEventListener('cp_load_public_pages', handleLoadPublicPages)
globalThis.addEventListener('cp_logout_successful', handleLogout)

onMounted(async () => {
  globalThis.addEventListener('resize', onResize)
  onResize()
  checkStateOfParentHeaders()

  globalThis.dispatchEvent(
    new CustomEvent('cp_loaded_successful', {
      bubbles: true,
      detail: {}
    })
  )

  loading.value = false
})

onUnmounted(() => {
  globalThis.removeEventListener('resize', onResize)
  globalThis.removeEventListener('cp_keycloak_auth_success', handleKeycloakSuccess)
  globalThis.removeEventListener('cp_keycloak_auth_fail', handleKeycloakFail)
  globalThis.removeEventListener('cp_load_public_pages', handleLoadPublicPages)
  globalThis.removeEventListener('cp_logout_successful', handleLogout)
})

function onResize() {
  isDesktopNavi.value = isDa()
    ? globalThis.innerWidth > 1020
    : globalThis.innerWidth > 991
}

function checkStateOfParentHeaders(): void {
  /* check this stuff; pass in selectors as props? */
  const header =
    (document.querySelector('#header') as HTMLElement) ||
    (globalThis.document
      .querySelector('#host')
      ?.shadowRoot?.querySelector('#header') as HTMLElement)
  const mobileHeader =
    (document.querySelector('#header .mobile-head') as HTMLElement) ||
    (globalThis.document
      .querySelector('#host')
      ?.shadowRoot?.querySelector('#header .mobile-head') as HTMLElement)
  /*check if mobile header exits, is visible*/
  if (
    header &&
    mobileHeader &&
    (getComputedStyle(mobileHeader).display !== 'none' ||
      getComputedStyle(header).position === 'fixed')
  ) {
    parentHasStickyHeader.value = true
    const mobileHeaderHeight = mobileHeader.offsetHeight
    const headerHeight = header.offsetHeight
    /* get maximum offset for navigation */
    offsetForNavigation.value = Math.max(mobileHeaderHeight, headerHeight)
    /* set offset for headline only if nescessary */
    paddingForHeadline.value = headerHeight > 0 ? '0' : `${mobileHeaderHeight}px`
  } else {
    parentHasStickyHeader.value = false
    offsetForNavigation.value = 0
    paddingForHeadline.value = '0'
  }
  positionStyle.value = parentHasStickyHeader.value ? 'fixed' : undefined
}

function clickNaviLink(event: Event): void {
  const menuItemPath = (event.target as HTMLInputElement).dataset.path
  if (menuItemPath) clickNaviLinkPath(menuItemPath)
}

function clickNaviLinkPath(path: string): void {
  const menuItem = path ? getRouteItem(path) : null
  if (menuItem && menuItem.path) {
    router.push(menuItem.path)
  }
}
</script>

<style lang="scss" scoped>
.extra-margin-zkp {
  margin-bottom: var(--head-paragraph-top-mobile);
}

header {
  line-height: 1.5;
  max-height: 100vh;
  margin: 16px 0;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
  margin-top: 2rem;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
}

nav a:first-of-type {
  border: 0;
}

@media (min-width: 1024px) {
  header {
    display: flex;
    place-items: center;
  }

  .logo {
    margin: 0 2rem 0 0;
  }

  header .wrapper {
    display: flex;
    place-items: flex-start;
    flex-wrap: wrap;
  }

  nav {
    text-align: left;
    margin-left: -1rem;
    font-size: 1rem;

    padding: 1rem 0;
    margin-top: 1rem;
  }
}

@media (min-width: 600px) {
  .navigation-mobile.navigation-overview {
    background: var(--header-bg-desktop);
  }
}
</style>
